import { useState, useEffect, useRef } from 'react';
import uniqueId from 'lodash.uniqueid';
import propTypes from 'prop-types';
import { AsyncTypeahead, Menu, MenuItem, Highlighter } from 'react-bootstrap-typeahead';
import { StyledTextInputWrapper } from '../../shared/FormComponents/TextInput';
import { device } from '../../../utils/styled-component-mixins';
import useMediaQuery from '../../custom-hooks/useMediaQuery';
import { toggleMobileSearchMenu } from '../../../features/search';
import { SEARCH_QUERY_PARAMS } from '../../utils/search-query-param-constants';

const SearchTypeAhead = (props) => {
  const { searchInputLabel, id, renderCustomInput } = props;
  const typeAheadRef = useRef(null);

  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [seeAllResultsLinkText, setSeeAllResultsLinkText] = useState('');
  const [searchLabel, setSearchLabel] = useState('');
  const [typeaheadCategoryPreposition, setTypeaheadCategoryPreposition] = useState('');
  const [servicesCategoryText, setServicesCategoryText] = useState('');
  const [industriesCategoryText, setIndustriesCategoryText] = useState('');
  const [insightsCategoryText, setInsightsCategoryText] = useState('');
  const [showChild, setShowChild] = useState(false); // needed for ssr of react-bootstrap-typeahead
  const [initialSearchValue, setInitialSearchValue] = useState('');
  const isDesktop = useMediaQuery(device.desktop);
  const uniqueIdentifier = uniqueId();
  const { SEARCH_TEXT, FILTER, SORT } = SEARCH_QUERY_PARAMS;

  useEffect(() => {
    setSeeAllResultsLinkText(window.searchSeeAllResultsLinkText);
    setSearchLabel(window.searchLabel);
    setTypeaheadCategoryPreposition(window.searchTypeAheadCategoryPreposition);
    setServicesCategoryText(window.typeAheadServiceCategoryText);
    setIndustriesCategoryText(window.typeAheadIndustriesCategoryText);
    setInsightsCategoryText(window.typeAheadInsightsCategoryText);
  }, []);

  const mapResultClassName = (className) => {
    switch (className) {
      case 'BDO.InsightNode':
        return insightsCategoryText;
      case 'BDO.ServiceNode':
        return servicesCategoryText;
      case 'BDO.ServiceAreaNode':
        return servicesCategoryText;
      case 'BDO.IndustryNode':
        return industriesCategoryText;
      default:
        return null;
    }
  };

  const removeResultsWithNoPageTitle = (results) => results.filter((result) => result.pagetitle);

  const mapResults = (results) =>
    results.map((result) => ({
      ...result,
      categoryDisplayName: mapResultClassName(result.classname)
    }));

  const handleTypeAhead = (searchString = '', pageSize = 10, pageNumber = 1, type = '') => {
    if (searchString.length > 2) {
      setIsLoading(true);
      fetch(
        `/api/search/${pageSize}/${pageNumber}?searchstring=${encodeURIComponent(
          searchString
        )}&type=${type}&sortby=${props.defaultSortBy}`
      )
        .then((response) => response.json())
        .then((data) => {
          if (data.results.length <= 0) {
            setOptions([]);
          } else {
            const filteredResults = removeResultsWithNoPageTitle(data.results);
            const mappedResults = mapResults(filteredResults);
            const resultsWithSeeAllLink = [
              ...mappedResults,
              {
                pagetitle: `${seeAllResultsLinkText} '${searchString}'`,
                url: `/search?${SEARCH_TEXT}=${encodeURIComponent(
                  searchString
                )}&sortby=top-results`,
                nodeguid: 'see-all' // used for the react key
              }
            ];

            setOptions(resultsWithSeeAllLink);
          }
          setIsLoading(false);
        })
        .catch((error) => {
          console.error(error);
          setIsLoading(false);
        });
    }
  };

  const sortByMapping = {
    0: 'recent',
    1: 'top-results'
  };

  const handleSearchPageNavigation = (searchString) => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const type = urlParams.get(FILTER) ? urlParams.get(FILTER) : '';
    const sortBy = urlParams.get(SORT) ? urlParams.get(SORT) : 'top-results';

    if (window.location.pathname === '/search') {
      // update url without reloading page
      const newUrl = `/search?${SEARCH_TEXT}=${encodeURIComponent(
        searchString
      )}&${FILTER}=${type}&${SORT}=${sortBy}`;
      window.history.pushState({ path: newUrl }, '', newUrl);

      if (props.searchPageTypeAheadEnterHandler !== undefined) {
        props.searchPageTypeAheadEnterHandler(searchString);
      }

      typeAheadRef.current.blur();
    } else {
      window.location.href = `/search?${SEARCH_TEXT}=${encodeURIComponent(
        searchString
      )}&${SORT}=${encodeURIComponent(sortByMapping[1])}`;
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const input = e.target.querySelectorAll('.rbt-input-main.form-control.rbt-input')[0].value;

    if (!isDesktop && window.location.pathname !== '/search') {
      toggleMobileSearchMenu();
    }

    handleSearchPageNavigation(input);
  };

  const handleOptionSelect = (option) => {
    window.location.href = option.url;
  };

  const handleEnterKeyDown = (e, optionsList) => {
    if (e.key === 'Enter') {
      const searchValue = e.target.value;
      const matchingIndex = optionsList.findIndex((option) => option.pagetitle === searchValue);

      if (matchingIndex > -1) {
        window.location.href = optionsList[matchingIndex].url;
      } else {
        handleTypeAhead(searchValue);
        handleSearchPageNavigation(searchValue);
      }

      if (!isDesktop && window.location.pathname !== '/search') {
        toggleMobileSearchMenu();
      }
    }
  };

  const toggleHeaderZIndex = (isOpen) => {
    const header = document.querySelector('header');
    if (isOpen) {
      header.style.setProperty('--header-z-index', '520');
    } else {
      header.style.setProperty('--header-z-index', '510');
    }
  };

  const renderInputProp = renderCustomInput
    ? {
        renderInput: ({ inputRef, referenceElementRef, ...inputProps }) => (
          <StyledTextInputWrapper hasValue={!!inputProps.value}>
            <input
              id={id + uniqueIdentifier}
              {...inputProps}
              className={`${inputProps.className} rbt-input-main form-control rbt-input`}
              ref={(input) => {
                inputRef(input);
                referenceElementRef(input);
              }}
            />
            <label htmlFor={id + uniqueIdentifier}>{searchLabel}</label>
          </StyledTextInputWrapper>
        )
      }
    : {};

  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const searchValue = urlParams.get(SEARCH_TEXT) ? urlParams.get(SEARCH_TEXT) : '';
    setInitialSearchValue({ pagetitle: searchValue });
    setShowChild(true);
  }, []);

  return (
    <>
      {showChild && (
        <form
          id={`search-form${uniqueIdentifier}`}
          onSubmit={handleFormSubmit}
          aria-label="site search"
        >
          <AsyncTypeahead
            ref={typeAheadRef}
            className="js-typeahead-search"
            labelKey={(option) => option.pagetitle}
            filterBy={() => true} // tell typeahead to disable filtering and show all results
            id={id + uniqueIdentifier}
            aria-label={searchInputLabel || searchLabel}
            isLoading={isLoading}
            options={options}
            placeholder={searchInputLabel || searchLabel}
            minLength={3}
            onMenuToggle={(isOpen) => toggleHeaderZIndex(isOpen)}
            onKeyDown={(e) => handleEnterKeyDown(e, options)}
            onSearch={handleTypeAhead}
            defaultSelected={[initialSearchValue]}
            useCache={false}
            delay={0}
            {...renderInputProp}
            renderMenu={(results, menuProps, typeAheadProps) => (
              <Menu
                aria-label={menuProps['aria-label']}
                children={menuProps.children}
                data-popper-escaped={menuProps['data-popper-escaped']}
                id={menuProps.id + uniqueIdentifier}
                innerRef={menuProps.innerRef}
                data-popper-placement={menuProps['data-popper-placement']}
                style={menuProps.style}
                data-popper-reference-hidden={menuProps['data-popper-reference-hidden']}
              >
                {results.map((result, index) => (
                  <MenuItem
                    option={result}
                    position={index}
                    aria-label={result.pagetitle}
                    onClick={() => handleOptionSelect(result)}
                    key={result.nodeguid}
                    href={result.url}
                  >
                    <div className="ellipsis-container">
                      <span className="ellipsis-container-left">
                        <Highlighter search={typeAheadProps.text}>{result.pagetitle}</Highlighter>
                      </span>
                      {index !== results.length - 1 && result.categoryDisplayName && (
                        <span className="ellipsis-container-right">
                          {' '}
                          {typeaheadCategoryPreposition} {result.categoryDisplayName}
                        </span>
                      )}
                    </div>
                  </MenuItem>
                ))}
              </Menu>
            )}
          >
            {() => (
              <div className="rbt-aux">
                <button type="submit" htmlFor={`search-form${uniqueIdentifier}`}>
                  <span className="material-icons-round">search</span>
                </button>
              </div>
            )}
          </AsyncTypeahead>
        </form>
      )}
    </>
  );
};

SearchTypeAhead.propTypes = {
  id: propTypes.string.isRequired
};

SearchTypeAhead.defaultProps = {
  defaultSortBy: 'top-results'
};

export default SearchTypeAhead;
